import React from "react";
import { cn, dateLocale } from "@/lib/utils";

import { translateFr as tfr } from "@/i18n";
export const teamInitials = (name = "") =>
  name.split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 3).join("").toUpperCase() || "?";

export const TeamLogo = ({ club, size = "h-14 w-14", text = "text-lg" }) => {
  const [err, setErr] = React.useState(false);
  const url = club?.id ? `${process.env.REACT_APP_BACKEND_URL}/api/clubs/${club.id}/logo` : null;
  if (url && !err) {
    return <img src={url} alt="" onError={() => setErr(true)} className={cn(size, "rounded-xl object-contain bg-secondary border border-border shrink-0")} />;
  }
  return (
    <span className={cn(size, text, "rounded-xl bg-secondary border border-border flex items-center justify-center font-semibold text-foreground/80 shrink-0")} aria-hidden data-testid="team-logo-placeholder">
      {teamInitials(club?.name)}
    </span>
  );
};

export const Section = ({ title, action, children, testid, className }) => (
  <section className={cn("rounded-2xl border border-border bg-card shadow-sm dark:shadow-none p-4", className)} data-testid={testid}>
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
      <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

export const Empty = ({ text, cta, onClick, testid }) => (
  <div className="rounded-xl border border-dashed border-border p-6 text-center" data-testid={testid}>
    <p className="text-xs text-muted-foreground">{text}</p>
    {cta && (
      <button onClick={onClick} className="mt-2 text-xs font-medium text-athlynx hover:underline" data-testid={testid ? `${testid}-cta` : undefined}>
        {cta}
      </button>
    )}
  </div>
);

export const fmtD = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString(dateLocale(), { day: "numeric", month: "short", year: "numeric" });
};
export const daysAgo = (iso) => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
};
export const pInitials = (n = "") => n.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

export const LINK_STATUSES = ["Au club", "Essai", "Transfert", "Prêt", "Fin de contrat", "Ciblé", "Ancien"];
export const REL_STATUSES = ["Prospect", "Contact établi", "Relation active", "Partenaire", "Inactif", "À réactiver"];
export const REL_PRIORITIES = ["Faible", "Normale", "Haute", "Stratégique"];
export const COLLAB_AREAS = ["Recrutement jeunes", "Suivi de joueurs", "Partenariat académie", "Échange de données", "Événements / visibilité"];
export const OPP_STAGES = ["Prospection", "En discussion", "En attente", "Gagnée", "Perdue"];
export const OPP_TYPES = ["Recrutement", "Transfert", "Prêt", "Partenariat", "Sponsoring", "Formation", "Autre"];
export const DOC_CATEGORIES = ["Contrats", "Collaborations", "Scouting", "Administratif", "Autre"];
export const ACT_TYPE_LABELS = {
  call: "Appel", meeting: tfr("Réunion"), email: "Email", message: "Message", note: "Note",
  follow_up: tfr("Relance"), document: "Document", opportunity: tfr("Opportunité"), athlete: tfr("Athlète"),
  contact: "Contact", other: "Autre",
};
export const STAGE_TONE = {
  Prospection: "border-border text-muted-foreground bg-secondary/50",
  "En discussion": "border-blue-500/40 text-blue-700 bg-blue-500/10 dark:text-blue-300",
  "En attente": "border-amber-500/40 text-amber-700 bg-amber-500/10 dark:text-amber-300",
  "Gagnée": "border-emerald-500/40 text-emerald-700 bg-emerald-500/10 dark:text-emerald-300",
  "Perdue": "border-rose-500/40 text-rose-700 bg-rose-500/10 dark:text-rose-300",
};

// Profile/setup completion — semantic color mapping (NOT relationship health)
export const completionTone = (pct) => {
  if (pct <= 25) return { key: "danger", bar: "bg-rose-500", glow: "shadow-[0_0_8px_-1px] shadow-rose-500/40", text: "text-rose-700 dark:text-rose-400" };
  if (pct <= 50) return { key: "warning", bar: "bg-orange-500", glow: "shadow-[0_0_8px_-1px] shadow-orange-500/40", text: "text-orange-700 dark:text-orange-400" };
  if (pct <= 70) return { key: "amber", bar: "bg-amber-500", glow: "shadow-[0_0_8px_-1px] shadow-amber-500/40", text: "text-amber-700 dark:text-amber-400" };
  if (pct <= 85) return { key: "info", bar: "bg-blue-500", glow: "shadow-[0_0_8px_-1px] shadow-blue-500/40", text: "text-blue-700 dark:text-blue-400" };
  return { key: "success", bar: "bg-emerald-500", glow: "shadow-[0_0_8px_-1px] shadow-emerald-500/40", text: "text-emerald-700 dark:text-emerald-400" };
};

export const SemanticProgress = ({ value, label, title, className, testid }) => {
  const tone = completionTone(value);
  return (
    <div role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100} aria-label={label} title={title}
      className={cn("h-1.5 rounded-full bg-secondary overflow-hidden", className)} data-testid={testid}>
      <div className={cn("h-full rounded-full transition-[width,background-color] duration-300 motion-reduce:transition-none", tone.bar, tone.glow)}
        style={{ width: `${value}%` }} />
    </div>
  );
};
